/**
 * MovesPalette
 *
 * Phase 3 UI for the moves engine. Visible only when exactly one chord is
 * selected (the parent passes a MoveContext, or null to hide). Shows only the
 * moves that are applicable to that chord as "specimen tags" (Fieldbook §6) —
 * inapplicable moves are hidden, never disabled. Applying a move runs the whole
 * engine -> revoice -> commit -> history flow in the parent; afterwards this
 * panel shows the move's explanation in handwriting voice with undo / hear-it /
 * hear-the-old-way.
 */
import React from 'react';
import { MOVES, type Move, type MoveContext } from '@/lib/moves';
import { washForMove } from '@/lib/moves/function-map';
import { useMovesStore } from '@/store/moves-store';
import styles from './MovesPalette.module.css';

interface MovesPaletteProps {
  /** The selected-chord context, or null when the palette should be hidden. */
  context: MoveContext | null;
  onApplyMove: (move: Move) => void;
  onUndo: () => void;
  onHearNew: () => void;
  onHearOld: () => void;
}

const MovesPaletteComponent: React.FC<MovesPaletteProps> = ({
  context,
  onApplyMove,
  onUndo,
  onHearNew,
  onHearOld,
}) => {
  const lastApplied = useMovesStore((s) => s.lastApplied);

  if (!context) return null;

  const applicable = MOVES.filter((move) => move.applicable(context));

  return (
    <div className={styles.palette} role="group" aria-label="Moves">
      <div className={styles.header}>moves</div>

      {applicable.length === 0 ? (
        <p className={styles.emptyNote}>nothing to try on this one yet</p>
      ) : (
        <ul className={styles.tagList}>
          {applicable.map((move) => {
            // The applied move is the active tag (foundation §6). Its lifetime
            // is the explanation strip's: both are cleared when the selection
            // changes, so the tint never outlives the sentence explaining it.
            const isActive = lastApplied?.moveId === move.id;
            const wash = isActive ? washForMove(move.id) : null;

            return (
              <li key={move.id}>
                <button
                  type="button"
                  className={[
                    styles.tag,
                    isActive ? styles.tagActive : '',
                    wash ? styles.tagWashed : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  // Not colour alone: the applied tag is announced, and the
                  // 2px ink border carries it for anyone who cannot see hue.
                  aria-current={isActive ? 'true' : undefined}
                  style={wash ? ({ '--move-wash': wash } as React.CSSProperties) : undefined}
                  onClick={() => onApplyMove(move)}
                >
                  <span className={styles.tagName}>{move.name}</span>
                  <span className={styles.tagTheory}>{move.theory}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {lastApplied && (
        <div className={styles.strip}>
          <p className={styles.explanation}>{lastApplied.explanation}</p>
          <p className={styles.theoryLabel}>{lastApplied.theoryLabel}</p>
          <div className={styles.stripActions}>
            <button type="button" className={styles.link} onClick={onUndo}>
              undo
            </button>
            <button type="button" className={styles.link} onClick={onHearNew}>
              hear it
            </button>
            <button type="button" className={styles.link} onClick={onHearOld}>
              hear the old way
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

MovesPaletteComponent.displayName = 'MovesPalette';

export const MovesPalette = React.memo(MovesPaletteComponent);
